import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { fetchCalendarV2EventsByDay } from ".";
import { useCalendarV2LiveFeed } from "./useCalendarV2LiveFeed";

const dateKeyFor = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;

/**
 * Fetches the selected day's calendar events from the new backend via React
 * Query, then keeps them live via a dedicated day-scoped SSE
 * (useCalendarV2LiveFeed) that upserts straight into the query cache. Mirrors
 * the shape of the legacy useGetCalendarEvents so the table can consume it the
 * same way. `staleTime: Infinity` — freshness comes from the SSE, not polling.
 */
export const useGetCalendarV2Events = (d: Date) => {
  const dateKey = dateKeyFor(d);

  const query = useQuery({
    queryKey: ["calendarV2Events", dateKey],
    queryFn: () => fetchCalendarV2EventsByDay(d),
    staleTime: Infinity,
  });

  useCalendarV2LiveFeed(d, dateKey, query.isSuccess);

  const sortedData = useMemo(() => {
    return query.data
      ?.slice()
      .sort(
        (a, b) =>
          new Date(a.occurs_at).getTime() - new Date(b.occurs_at).getTime()
      );
  }, [query.data]);

  return {
    ...query,
    data: sortedData,
  };
};
